<template>
  <div
    :class="[
      $style.root,
      {
        [$style.typeInformative]: type === 'informative',
        [$style.typeSuccess]: type === 'success',
        [$style.typeWarning]: type === 'warning',
        [$style.typeError]: type === 'error',

        [$style.closeButtonPadding]: hasCloseButton,
      },
    ]"
  >
    <SCASvgIcon v-if="!noIcon" :class="$style.icon" :icon="usedIcon" />
    <div :class="$style.contentContainer">
      <template v-if="title || description">
        <div v-if="title" :class="$style.title">{{ title }}</div>
        <div v-if="description" :class="$style.description">{{ description }}</div>
      </template>
      <div :class="$style.actionContainer">
        <slot v-if="$slots['default']" />
      </div>
    </div>
    <SCAIconButton
      v-if="hasCloseButton"
      :class="$style.closeButton"
      icon="phosphor/x"
      type="ghost"
      appearance="secondary"
      size="small"
      @click="emit('close')"
    />
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";

import type { IconName } from "./icons/iconNames.generated";
import SCAIconButton from "./SCAIconButton.vue";
import SCASvgIcon from "./SCASvgIcon.vue";

type Type = "informative" | "success" | "warning" | "error";

const props = withDefaults(
  defineProps<{
    type?: Type;
    title?: string;
    description?: string;
    hasCloseButton?: boolean;
    noIcon?: boolean;
    icon?: IconName;
  }>(),
  {
    type: undefined,
    title: undefined,
    description: undefined,
    hasCloseButton: false,
    noIcon: false,
    icon: undefined,
  },
);
const emit = defineEmits<{
  close: [];
}>();
const usedIcon = computed(() => {
  if (props.icon) {
    return props.icon;
  }
  switch (props.type) {
    case "informative":
      return "phosphor/info-fill";
    case "success":
      return "phosphor/check-circle-fill";
    case "warning":
      return "phosphor/warning-fill";
    case "error":
      return "phosphor/warning-diamond-fill";
    default:
      return "phosphor/info-fill";
  }
});
</script>

<style module lang="scss">
@use "../styles/generated/figma_typo_mixins.scss" as FigmaTypoMixins;

.root {
  display: flex;
  padding: var(--space-component-padding-xLarge);
  gap: var(--space-component-gap-large);
  width: 100%;
  min-width: min-content;
  align-items: flex-start;
  justify-content: flex-end;
  position: relative;

  border-radius: var(--border-radius-large);
  color: var(--color-content-secondary);
  border: 1px solid var(--color-border-neutral-subtle);
  background: var(--color-background-neutral-subtle);

  @media (max-width: 768px) {
    padding: var(--space-component-padding-medium);
  }

  &.closeButtonPadding {
    padding-right: var(--space-component-padding-6xLarge);
  }

  &.typeInformative {
    color: var(--color-content-informative-secondary);
    background: var(--color-background-informative-subtle);
    border: 1px solid var(--color-border-informative-subtle);
    .title {
      color: var(--color-content-informative-primary);
    }
  }
  &.typeSuccess {
    color: var(--color-content-positive-secondary);
    background: var(--color-background-positive-subtle);
    border: 1px solid var(--color-border-positive-subtle);
    .title {
      color: var(--color-content-positive-primary);
    }
  }
  &.typeWarning {
    color: var(--color-content-warning-secondary);
    background: var(--color-background-warning-subtle);
    border: 1px solid var(--color-border-warning-subtle);
    .title {
      color: var(--color-content-warning-primary);
    }
  }
  &.typeError {
    color: var(--color-content-negative-secondary);
    background: var(--color-background-negative-subtle);
    border: 1px solid var(--color-border-negative-subtle);
    .title {
      color: var(--color-content-negative-primary);
    }
  }
}

.title {
  @include FigmaTypoMixins.typo-body-large;
  color: var(--color-content-primary);
}

.description {
  @include FigmaTypoMixins.typo-body-medium;
  color: var(--color-content-secondary);
}

.contentContainer {
  display: flex;
  flex-direction: column;
  gap: var(--space-component-gap-large);
  flex: 1 0 0;
}

.closeButton {
  color: var(--color-action-neutral-normal);

  position: absolute;
  right: var(--dimension-200);
  top: var(--dimension-200);
}

.actionContainer {
  display: flex;
  align-items: center;
  gap: var(--space-component-gap-small);
  align-self: stretch;

  @media (max-width: 1280px) {
    flex-direction: column;
    align-items: flex-start;
    gap: var(--space-component-gap-medium);
  }

  @media (max-width: 768px) {
    align-items: stretch;
  }
}

.icon {
  margin-top: 4px;
}
</style>
